import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
// import ExamConfiguration from './ExamConfiguration';
import FilterStudents from './FilterStudents';
import AvailableStudents from './AvailableStudents';
import MarksEntryForm from './MarksEntryForm';
import { useDispatch, useSelector } from 'react-redux';
import { getExaminationsList } from '../../features/examinations/examinationSlice';
import { getClasses } from '../../features/classes/ClassSlice';
import {
  createMarksEntry,
  getStudentsByFiltersForMarks,
} from '../../features/marks/marksSlice';
import { fetchGrades } from '../../features/grades/gradeSlice';

import ToastNotification from '../UIElements/ToastNotification';
import useNotification from '../../hooks/useNotification';
import Spinner from '../UIElements/Spinner';

const MarksEntryTab = () => {
  const dispatch = useDispatch();

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState({
    examType: 'Select',
    department: 'Select',
    section: 'Select',
    class: 'Select',
  });
  const [departments, setDepartments] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isSubmittingMarks, setIsSubmittingMarks] = useState(false);

  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  const { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });

  // Filter students based on search and filters
  useEffect(() => {
    const filteredStudents =
      students?.studentMaks?.length > 0
        ? (() => {
            const normalizedSearch = searchTerm
              ?.toString()
              ?.trim()
              ?.toLowerCase();
            if (!normalizedSearch) return students.studentMaks;
            return students.studentMaks.filter((student) => {
              const matchesSearch =
                student?.studentName
                  ?.toLowerCase()
                  ?.includes(normalizedSearch) ||
                student?.studentId
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(normalizedSearch);
              return matchesSearch;
            });
          })()
        : [];
    setFilteredStudents(filteredStudents);
  }, [searchTerm, students]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleFiltersChange = (newFilters) => {
    setCurrentFilters(newFilters);
  };

  const handleSearch = async (searchFilters) => {
    if (isLoadingStudents) return;

    setIsLoadingStudents(true);
    setFilteredStudents([]);
    try {
      const res = await dispatch(
        getStudentsByFiltersForMarks({
          examType: searchFilters.examType,
          sectionName: searchFilters.section,
          classesName: searchFilters.class,
          departmentname: searchFilters.department,
          instituteType: searchFilters.instituteType,
        }),
      ).unwrap();
      setActiveFilters(searchFilters);
      setStudents(res || []);
      setFilteredStudents(res?.studentMarks || []);
      const isFindSelectedStudent = res?.studentMaks?.find(
        (s) => s.id === selectedStudent?.id,
      );
      if (!isFindSelectedStudent) {
        setSelectedStudent(null);
      } else {
        setSelectedStudent(isFindSelectedStudent);
      }
    } catch (error) {
      console.error('Error searching students:', error);
      showError('Failed to search students');
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const handleClearFilters = (clearedFilters) => {
    setCurrentFilters(clearedFilters);
    setFilteredStudents([]);
    setStudents([]);
    setSelectedStudent(null);
  };

  const handleMarksSubmit = async (student, marks) => {
    if (isSubmittingMarks) return;

    setIsSubmittingMarks(true);
    try {
      const payload = {
        id: student?.id,
        studentId: student?.studentId,
        studentName: student?.studentName,
        rollNumber: student?.rollNumber,
        examType: activeFilters.examType,
        departmentName: activeFilters.department,
        classesName: activeFilters.class,
        sectionName:
          instituteType?.toLowerCase() === 'college'
            ? activeFilters.section
            : '',
        instituteType: instituteType,
        marks: marks,
      };
      const res = await dispatch(createMarksEntry(payload)).unwrap();
      if (res) {
        showSuccess('Marks entry created successfully.');
        await handleSearch(activeFilters);
      } else {
        showError('Failed to create marks entry.');
      }
    } catch (error) {
      console.error('Error submitting marks:', error);
      showError('Failed to create marks entry.');
    } finally {
      setIsSubmittingMarks(false);
    }
  };
  const getFilters = async () => {
    setIsLoadingFilters(true);
    try {
      const examTypesRes = await dispatch(getExaminationsList()).unwrap();
      const departmentRes = await dispatch(getClasses()).unwrap();
      const grades = await dispatch(fetchGrades()).unwrap();
      setExamTypes(examTypesRes || []);
      setDepartments(departmentRes || []);

      setGrades(grades || []);
      // To remove  the default selected  grade
      // if (departmentRes?.length > 0) {
      //   setCurrentFilters((prev) => ({
      //     ...prev,
      //     department: departmentRes[0]?.department || 'Select',
      //   }));
      // }
    } catch (error) {
      console.error('Error loading filters:', error);
      showError('Failed to load filters');
    } finally {
      setIsLoadingFilters(false);
    }
  };

  useEffect(() => {
    getFilters();
  }, [dispatch]);

  return (
    <div
      className='space-y-6'
      style={{ width: '100%', minHeight: '100vh', boxSizing: 'border-box' }}
    >
      {/* Exam Configuration */}
      {/* <ExamConfiguration
            filters={filters}
            onFiltersChange={handleFiltersChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
        /> */}

      {/* Filter Students */}
      {isLoadingFilters ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}
        >
          <Spinner size='large' />
        </div>
      ) : (
        <FilterStudents
          departments={departments}
          examTypes={examTypes}
          initialFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          onClearFilters={handleClearFilters}
        />
      )}

      {/* Responsive Two Column Layout */}
      <Grid
        container
        spacing={4}
        sx={{
          width: '100%',
          margin: 0,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {/* Available Students */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '30%' },
            minHeight: { xs: 300, md: 500 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isLoadingStudents ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px',
              }}
            >
              <Spinner size='large' />
            </div>
          ) : (
            <AvailableStudents
              students={filteredStudents}
              selectedStudent={selectedStudent}
              onStudentSelect={handleStudentSelect}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          )}
        </Grid>

        {/* Selected Student / Marks Entry */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '70%' },
            minHeight: { xs: 300, md: 500 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MarksEntryForm
            selectedStudent={selectedStudent}
            onMarksSubmit={handleMarksSubmit}
            examType={currentFilters?.examType}
            department={students?.departmentName}
            class={students?.classesName}
            section={students?.sectionName}
            instituteType={instituteType}
            isSubmitting={isSubmittingMarks}
            grades={grades}
          />
        </Grid>
      </Grid>

      {/* Toast Notification */}
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </div>
  );
};

export default MarksEntryTab;
